import { type IntlShape } from 'react-intl-next';

import { type AutoCompleteItem } from '@atlassian/eoc-auto-complete';
import { type ResponderItem } from '@atlassian/eoc-responder-names';

import { messages as commonMessages } from '../../common/messages';
import { type RequestedResponder, type ResponderType } from '../../common/types';
import { isEmpty } from '../validators';

import { REQUESTED_NO_ONE } from './constants';
import { type AutoCompleteWithNoOneItem } from './types';

// NOTE: this type copied from responder-names as is
type LoadedResponderItem = {
	id: string;
	type: ResponderType;
	deleted: boolean;
	name: string;
	email?: string;
	atlassianIdentifier?: string;
	isLoading: false;
	acknowledged: true;
	isError: false;
};

/**
 * Get predefined values of type "noone" responder
 * @returns [ResponderNamesItem, AutoCompleteWithNoOneItem]
 */
const getNoOne = (intlApi: IntlShape) =>
	[
		{
			id: REQUESTED_NO_ONE.id,
			type: REQUESTED_NO_ONE.type,
			name: intlApi.formatMessage(commonMessages.noOne),
			deleted: false,
		},
		{
			label: intlApi.formatMessage(commonMessages.noOne),
			type: REQUESTED_NO_ONE.type,
			value: REQUESTED_NO_ONE.id,
			// NOTE: All override made with atlassianIdentifier so assuming no-one has atlassianIdentifier
			atlassianIdentifier: REQUESTED_NO_ONE.id,
		},
	] as [LoadedResponderItem, AutoCompleteWithNoOneItem];

const getDeletedResponderName = (type: ResponderType<false>, intlApi: IntlShape) => {
	switch (type) {
		case 'user':
			return intlApi.formatMessage(commonMessages.formerUser);
		case 'escalation':
			return intlApi.formatMessage(commonMessages.deletedEscalation);
		case 'team':
		case 'group':
			return intlApi.formatMessage(commonMessages.teamDeleted);
		case 'schedule':
			return intlApi.formatMessage(commonMessages.deletedSchedule);
	}
};

export const getResponderName = (
	responder: ResponderItem | RequestedResponder<false>,
	intlApi: IntlShape,
) => {
	if ('name' in responder) {
		return responder.name;
	}

	if ('deleted' in responder && responder.deleted) {
		return getDeletedResponderName(responder.type, intlApi);
	}

	if (isEmpty(responder.id) && responder.type === 'team') {
		return intlApi.formatMessage(commonMessages.noneGlobalSchedule);
	}

	return '';
};

/**
 * Get responder with fallback. (Handles type "noone")
 */
const getResponder = (
	requested: RequestedResponder<true, true> | RequestedResponder,
	received: ResponderItem,
	intlApi: IntlShape,
) => {
	// NOTE: Schedule BE is source of truth for deleted status and type
	const { id, type } = requested;
	const deleted = 'deleted' in requested ? requested.deleted : false;

	if (type === 'noone') {
		const [noOneResponder] = getNoOne(intlApi);
		return noOneResponder;
	}

	const isReceivedLoadingOrError = received.isError || received.isLoading;
	if (isReceivedLoadingOrError) {
		return {
			id,
			type,
			deleted,
			name: deleted ? getDeletedResponderName(type, intlApi) : '',
			email: '',
		};
	}

	if (deleted) {
		return {
			...received,
			deleted,
			name: received.name ?? getDeletedResponderName(type, intlApi),
		};
	}

	return {
		...received,
		deleted,
	};
};

const filterNoOne = (
	items: (RequestedResponder<true> | RequestedResponder<false>)[],
): RequestedResponder<false>[] =>
	items.filter(
		(item) => item.type !== REQUESTED_NO_ONE.type,
	) as unknown as RequestedResponder<false>[];

const filterNoTeam = (
	items: (RequestedResponder<true> | RequestedResponder<false>)[],
): RequestedResponder<false>[] =>
	items.filter(
		(item) => !(item.type === 'team' && isEmpty(item.id)),
	) as unknown as RequestedResponder<false>[];

const getAutoCompleteItem = (responder?: ResponderItem): AutoCompleteItem | null => {
	if (!responder || responder.isError || responder.isLoading) {
		return null;
	}

	return {
		...responder,
		label: responder.name,
		value: responder.id,
	};
};

export { getResponder, getNoOne, getAutoCompleteItem, filterNoOne, filterNoTeam };
